import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  Fade,
  IconButton,
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  Stack,
  SxProps,
  styled,
} from '@mui/material';
import { useAppContext } from '@src/contexts';
import { useTheme } from '@src/themes';
import { FC, Fragment, PropsWithChildren, ReactNode } from 'react';
import { Header, Text } from '../text';

interface DialogProps {
  isLoading?: boolean;
  fullScreen?: boolean;
  mobilePartialFullScreen?: boolean;
  backgroundColor?: string;
}

const Dialog = styled(MuiDialog)<DialogProps>(({ isLoading, fullScreen, backgroundColor }) => ({
  '& .MuiDialog-paper': {
    padding: isLoading ? '88px 48px' : '24px 16px',
    background: backgroundColor,
    maxWidth: '488px',
    ...(fullScreen
      ? {}
      : {
          width: 'calc(100% - 32px)',
        }),
  },
  'h2': {
    display: isLoading ? 'none' : '',
  },
  '& .MuiDialogContent-root': {
    padding: 0,
  },
  '& .MuiDialogActions-root ': {
    display: isLoading ? 'none' : '',
    padding: '8px',
  },
}));

const DialogTitle = styled(MuiDialogTitle)(() => ({
  padding: '0 5px 0 5px',
}));

const DialogActions = styled(MuiDialogActions)(() => ({
  padding: '8px 5px',
}));

const DialogContent = styled(MuiDialogContent)(() => ({
  '& .MuiBox-root': {
    paddingRight: '5px',
    paddingLeft: '5px',
  },
  // it's not worth to modify firefox scrollbar
  '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
    background: 'inherit',
  },
  '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
    borderRadius: '10px',
  },
}));

interface ModalProps extends PropsWithChildren {
  opened: boolean;
  handleClose?: (v: boolean) => void;
  title?: ReactNode;
  headerContent?: ReactNode;
  actionsDirection?: 'row' | 'column';
  actions?: ReactNode;
  isLoading?: boolean;
  sxDialogContent?: SxProps;
  loadingTitle?: string;
  loadingMessage?: string;
  sxDialog?: SxProps;
  fullScreen?: boolean;
  titleNoWrap?: boolean;
  loaderHeight?: string;
  centerModalOnMobile?: boolean;
  mobilePartialFullScreen?: boolean;
  sxDialogActions?: SxProps;
  titleColor?: string;
}

const Modal: FC<ModalProps> = ({
  opened,
  handleClose,
  title,
  children,
  actions,
  actionsDirection,
  isLoading,
  sxDialogContent,
  headerContent,
  loadingTitle,
  loadingMessage,
  sxDialog,
  fullScreen,
  titleNoWrap,
  loaderHeight,
  centerModalOnMobile,
  mobilePartialFullScreen,
  sxDialogActions,
  titleColor,
}) => {
  const { isMobile } = useAppContext();
  const { COLORS } = useTheme();

  const getDialogProps = () => {
    if (fullScreen) return {};

    return {
      ...(mobilePartialFullScreen
        ? { width: { xs: '100%', md: 'calc(100% - 32px)' } }
        : { width: 'calc(100% - 32px)' }),
    };
  };

  return (
    <Dialog
      open={opened}
      isLoading={isLoading}
      onClose={() => null}
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={500}
      sx={{ ...sxDialog }}
      fullScreen={fullScreen ?? false}
      PaperProps={{
        sx: {
          mb: { xs: (centerModalOnMobile ?? true) ? 'auto' : 0, md: 'auto' },
          mx: 0,
          mt: 'auto',
          ...getDialogProps(),
        },
      }}
    >
      <DialogTitle style={{ position: 'sticky', marginBottom: headerContent !== undefined ? '0' : '16px' }}>
        <Fade in={!isLoading} timeout={500}>
          <Stack display={!isLoading ? 'flex' : 'none'} direction={'column'} width={'100%'}>
            <Stack
              display={'flex'}
              direction={'row'}
              width={'100%'}
              justifyContent={title ? 'space-between' : 'flex-end'}
              alignItems={'center'}
            >
              {Boolean(title) && (
                <Header variant={'h2'} color={titleColor ?? COLORS.text.primary} noWrap={titleNoWrap ?? true}>
                  {title}
                </Header>
              )}

              {!fullScreen && handleClose && (
                <IconButton onClick={() => handleClose(false)}>
                  <CloseIcon sx={{ fontSize: { md: 24, xs: 20 }, color: COLORS.ctas.secondary }} />
                </IconButton>
              )}
            </Stack>
            {headerContent !== undefined && <Fragment>{headerContent}</Fragment>}
          </Stack>
        </Fade>
      </DialogTitle>

      <DialogContent sx={{ ...sxDialogContent, overflow: isLoading ? 'hidden' : 'auto' }}>
        <Fade in={isLoading} timeout={500}>
          <Box
            sx={{
              display: isLoading ? 'flex' : 'none',
              height: loaderHeight ?? 'auto',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Header variant='h2' align='center' fontSize={24} marginBottom={4} color={COLORS.text.purpleHard}>
              {loadingTitle ?? 'Carregando...'}
            </Header>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 2, margin: 3 }}>
              <CircularProgress size='4rem' color='primary' />
            </Box>
            {loadingMessage && (
              <Stack width={'100%'} direction={'column'} display={'flex'} alignItems={'center'}>
                <Text align='center' fontSize={16} marginTop={4} marginBottom={4} color={COLORS.text.purpleHard}>
                  {loadingMessage}
                </Text>
              </Stack>
            )}
          </Box>
        </Fade>

        <Fade in={!isLoading} timeout={500}>
          <Box sx={{ display: !isLoading ? '' : 'none' }}>{children}</Box>
        </Fade>
      </DialogContent>

      <DialogActions sx={sxDialogActions}>
        <Fade in={!isLoading} timeout={500}>
          <Stack
            direction={actionsDirection ?? (isMobile ? 'column' : 'row')}
            gap={2}
            justifyContent={isMobile ? 'center' : 'space-between'}
            width={'100%'}
            display={!isLoading ? 'flex' : 'none'}
          >
            {actions}
          </Stack>
        </Fade>
      </DialogActions>
    </Dialog>
  );
};

export { Modal };
